// Business branding tokens — mirrors docs/BRANDING.md §3/§4 (binding contract).
// Components use `tok('slot')` (a CSS-variable reference with the default as
// fallback) instead of hardcoded colors, so every business looks like itself.

export const THEME_SLOTS = [
  'background', 'onBackground', 'onBackgroundMuted', 'logoBackdrop',
  'heroCard', 'onHeroCard', 'onHeroCardMuted', 'optionRow', 'onOptionRow',
  'dock', 'action', 'onAction', 'secondaryAction', 'onSecondaryAction',
  'surface', 'onSurface', 'onSurfaceMuted', 'card', 'cardBorder',
  'businessBubble', 'onBusinessBubble', 'customerBubble', 'onCustomerBubble',
  'accent', 'tile', 'onTile',
] as const;

export type ThemeSlot = (typeof THEME_SLOTS)[number];
export type BusinessThemeTokens = Record<ThemeSlot, string>;

export const DEFAULT_THEME: BusinessThemeTokens = {
  background: '#1B3A2D', onBackground: '#F5F0E8', onBackgroundMuted: '#F5F0E8CC', logoBackdrop: '#F5F0E8',
  heroCard: '#F5F0E8', onHeroCard: '#1B3A2D', onHeroCardMuted: '#2E5743',
  optionRow: '#F5F0E81A', onOptionRow: '#F5F0E8',
  dock: '#12291F', action: '#C47D4E', onAction: '#1A1A12',
  secondaryAction: '#F5F0E81F', onSecondaryAction: '#F5F0E8',
  surface: '#F5F3EC', onSurface: '#16241C', onSurfaceMuted: '#5E6A62',
  card: '#FFFFFF', cardBorder: '#E2DDCF',
  businessBubble: '#1B3A2D', onBusinessBubble: '#F5F0E8',
  customerBubble: '#FFFFFF', onCustomerBubble: '#16241C',
  accent: '#C47D4E', tile: '#1B3A2D', onTile: '#F5F0E8',
};

export interface BusinessCopy {
  eyebrow: string;
  helpPrompt: string;
  talkLabel: string;
  messagePlaceholder: string;
  talkSubtitle: string;
}

export function defaultCopy(displayName: string, category?: string): BusinessCopy {
  const name = displayName || 'us';
  return {
    eyebrow: (category || '').toUpperCase(),
    helpPrompt: 'How can we help?',
    talkLabel: 'Talk to us',
    messagePlaceholder: displayName ? `Message ${displayName}` : 'Type a message…',
    talkSubtitle: displayName
      ? `AI voice for ${name}. The team gets everything you say.`
      : 'AI voice assistant. The team gets everything you say.',
  };
}

const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Merge a (possibly partial / invalid) server theme over DEFAULT_THEME. */
export function mergeTheme(raw: unknown): BusinessThemeTokens {
  const out = { ...DEFAULT_THEME };
  if (raw && typeof raw === 'object') {
    for (const slot of THEME_SLOTS) {
      const v = (raw as Record<string, unknown>)[slot];
      if (typeof v === 'string' && HEX_RE.test(v.trim())) out[slot] = v.trim();
    }
  }
  return out;
}

export function mergeCopy(raw: unknown, displayName: string, category?: string): BusinessCopy {
  const base = defaultCopy(displayName, category);
  if (raw && typeof raw === 'object') {
    for (const k of Object.keys(base) as (keyof BusinessCopy)[]) {
      const v = (raw as Record<string, unknown>)[k];
      if (typeof v === 'string' && v.trim()) base[k] = v.trim();
    }
  }
  return base;
}

const kebab = (s: string) => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** CSS variable reference for a slot, with the default as fallback. */
export const tok = (slot: ThemeSlot): string => `var(--biz-${kebab(slot)}, ${DEFAULT_THEME[slot]})`;

export function themeToCssVars(theme: BusinessThemeTokens): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const slot of THEME_SLOTS) vars[`--biz-${kebab(slot)}`] = theme[slot];
  return vars;
}

/** Up to two initials from the business name. */
export function monogram(name: string): string {
  const words = name.replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '·';
  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return letters.toUpperCase();
}

/** Pin a full-screen customer page to the visible viewport. `dvh` excludes
    mobile browser toolbars (plain `vh` doesn't, which hides the bottom of the
    page behind Safari's toolbar); `vh` is the fallback for older browsers. */
export const FULL_SCREEN_SX = {
  height: '100vh',
  '@supports (height: 100dvh)': { height: '100dvh' },
  overflow: 'hidden',
} as const;

export const DISPLAY_FONT = '"Bricolage Grotesque", "DM Sans", system-ui, sans-serif';
export const BODY_FONT = '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
